import { StateCreator, create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * @deprecated Superseded by the `shifts.gate_required` permission
 * (migration 0508). The ShiftGateGuard and private theme now read
 * `useHasPermission('shifts.gate_required')` instead of matching
 * against this list. Kept only for backward-compat; delete once every
 * consumer is migrated.
 */
export const SHIFT_EXEMPT_ROLES = [
  'superadmin',
  'super_admin',
  'admin',
  'administrator',
  'manager',
  'vendor',
  'sommelier',
];

export type ShiftGateStoreSlice = {
  /**
   * Whether the user has passed the shift gate (has an open shift)
   */
  hasActiveShift: boolean;

  /**
   * The current shift ID if user has passed the gate
   */
  activeShiftId: string | null;

  /**
   * Set the shift gate state
   */
  setShiftGateState: (hasActiveShift: boolean, shiftId: string | null) => void;

  /**
   * Reset the shift gate state (on logout or shift close)
   */
  resetShiftGate: () => void;
};

export const DefaultShiftGateStore: Pick<
  ShiftGateStoreSlice,
  'hasActiveShift' | 'activeShiftId'
> = {
  hasActiveShift: false,
  activeShiftId: null,
};

const shiftGateStateSlice: StateCreator<
  ShiftGateStoreSlice,
  [['zustand/devtools', never], ['zustand/persist', unknown]],
  [],
  ShiftGateStoreSlice
> = (set) => ({
  ...DefaultShiftGateStore,

  setShiftGateState: (hasActiveShift: boolean, shiftId: string | null) =>
    set(
      () => ({ hasActiveShift, activeShiftId: shiftId }),
      undefined,
      'shiftGate/setShiftGateState'
    ),

  resetShiftGate: () =>
    set(() => DefaultShiftGateStore, undefined, 'shiftGate/resetShiftGate'),
});

export const useShiftGateStore = create<ShiftGateStoreSlice>()(
  devtools(persist(shiftGateStateSlice, { name: 'shift-gate' }), {
    name: 'shift-gate-store',
  })
);

/**
 * Check if a role name is exempt from shift gate
 */
export const isRoleExemptFromShiftGate = (
  roleName: string | undefined
): boolean => {
  if (!roleName) return false;
  const normalizedRole = roleName.toLowerCase().replace(/[\s-]/g, '_');
  return SHIFT_EXEMPT_ROLES.some(
    (exemptRole) =>
      normalizedRole === exemptRole ||
      normalizedRole.includes(exemptRole) ||
      exemptRole.includes(normalizedRole)
  );
};
